<template>

<div class="auth">

	<!-- standard header on every stage -->
	<app-mark class="auth-mark" />

	<!-- loading: validating the request + checking the session -->
	<div v-if="stage === 'loading'" class="auth-center">
		<div class="gg-spin"></div>
		<p class="auth-center-text">Just a moment…</p>
	</div>

	<!-- the request itself is bad (unknown client / unregistered redirect) -->
	<div v-else-if="stage === 'error'" class="auth-msg">
		<h1 class="gg-title auth-msg-title">Something’s not right</h1>
		<p class="gg-sub">{{ error }}</p>
	</div>

	<!-- reached directly, no app to sign in to -->
	<div v-else-if="stage === 'nostart'" class="auth-msg">
		<h1 class="gg-title auth-msg-title">geekgroup accounts</h1>
		<p class="gg-sub">This page is where apps sign you in with geekgroup. Head back to the app you came from and choose “Sign in with geekgroup”.</p>
	</div>

	<!-- not signed in -->
	<app-login v-else-if="stage === 'login'" :client-name="clientName" v-on:authed="onAuthed" v-on:cancel="onDeny" />

	<!-- signed in: confirm + continue -->
	<app-consent v-else-if="stage === 'consent'" :client="client" :profile="profile" :intent="intent" :busy="redirecting" :error="error" v-on:allow="onAllow" v-on:switch="onSwitch" v-on:deny="onDeny" />

</div>

</template>

<script>

import api from '../service/api'
import identity from '../service/identity'
import Mark from '../components/Mark.vue'
import Login from '../components/Login.vue'
import Consent from '../components/Consent.vue'

/*
	The OIDC authorization endpoint's UI. Validates the request, checks for an
	existing geekgroup session, runs login if needed, then consent — and on
	"Continue" asks the API to mint the code and sends the browser back to the
	client with ?code=&state=.
*/
export default {

	name: 'Authorize',

	components: { 'app-mark': Mark, 'app-login': Login, 'app-consent': Consent },

	data: function() {

		return {
			stage: 'loading',
			req: {},
			client: null,
			profile: null,
			token: false,
			redirecting: false,
			error: '',
			// a display hint from the client ('connect' when an already-signed-in
			// app is just linking the geekgroup account, not signing in fresh)
			intent: ''
		}

	},

	computed: {

		clientName: function() { return (this.client && this.client.name) || 'the app' }

	},

	created: function() {

		this.boot()

	},

	methods: {

		boot: function() {

			var self = this
			var q = this.$route.query

			if (!q.client_id) { this.stage = 'nostart'; return }

			this.intent = q.intent || ''

			this.req = {
				client_id: q.client_id,
				redirect_uri: q.redirect_uri || '',
				response_type: q.response_type || 'code',
				scope: q.scope || 'openid',
				state: q.state || '',
				nonce: q.nonce || '',
				code_challenge: q.code_challenge || '',
				code_challenge_method: q.code_challenge_method || 'S256'
			}

			// resolve the client + redirect BEFORE trusting either
			api.client(this.req).then(function(res) {

				if (!res || res.error || !res.client_id) {

					self.error = 'That app isn’t recognised. For your safety we won’t continue. Head back and try again.'
					self.stage = 'error'
					return

				}

				if (!res.redirect_ok) {

					self.error = 'This app’s return address isn’t registered with geekgroup, so we can’t send you back safely.'
					self.stage = 'error'
					return

				}

				self.client = res
				self.resolveSession()

			})

		},

		// reuse an existing geekgroup session if there is one
		resolveSession: function() {

			var self = this
			var token = identity.geekgroup()

			if (!token) { this.stage = 'login'; return }

			api.recognise(token).then(function(res) {

				if (res && res.user) {

					self.token = token
					self.profile = self.mapProfile(res)
					self.stage = 'consent'

				} else {

					// a stale/invalid cookie — drop it and ask for login
					identity.clear()
					self.stage = 'login'

				}

			})

		},

		onAuthed: function(token) {

			var self = this

			identity.set(token)
			this.token = token

			api.recognise(token).then(function(res) {

				self.profile = self.mapProfile(res)
				self.stage = 'consent'

			})

		},

		onAllow: function() {

			var self = this

			this.redirecting = true
			this.error = ''

			api.grant(this.req, this.token).then(function(res) {

				if (res && res.redirect) { window.location.href = res.redirect; return }

				// shouldn't happen (we validated up front) — surface, don't hang
				self.redirecting = false
				self.error = (res && res.error_description) || 'We couldn’t complete the sign-in. Try again.'

			})

		},

		onSwitch: function() {

			identity.clear()
			this.token = false
			this.profile = null
			this.error = ''
			this.stage = 'login'

		},

		// user declined: hand the client a standard access_denied
		onDeny: function() {

			var uri = this.req.redirect_uri
			var sep = (uri.indexOf('?') === -1) ? '?' : '&'
			var qs = 'error=access_denied'

			if (this.req.state) qs += '&state=' + encodeURIComponent(this.req.state)

			window.location.href = uri + sep + qs

		},

		mapProfile: function(res) {

			return {
				username: res.user,
				name: res.fullname,
				avatar: res.avatar,
				games: res.games,
				plays: res.plays,
				ratings: res.ratings,
				hindex: res.hindex
			}

		}

	}

}

</script>

<style scoped>

.auth {
	width: 380px;
	max-width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.auth-mark { margin-bottom: 26px; }

.auth-center {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	padding: 24px 0;
}

.auth-center-text { color: var(--muted); font-size: 14px; }

.auth-msg {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
}

.auth-msg-brand { margin-bottom: 22px; }

.auth-msg-title { margin-bottom: 2px; }

</style>
